<template>
  <div
    class="metrics-student-view"
    :class="{ 'is-exporting-pdf': isExportingPdf }"
  >
    <metrics-class-header
      :title="studentName"
      :is-demo-mode="isDemoMode"
      :is-page-loading="isPageLoading"
      :show-regenerate="true"
      @update:isDemoMode="onDemoModeChange"
      @regenerate="onRegenerate"
      @export-pdf="exportToPdf"
      @print="print"
    />

    <metrics-student-filters
      :assignments="availableAssignments"
      :selected-assignments="selectedAssignmentIds"
      :time-filter="timeFilter"
      @update:selectedAssignments="onSelectedAssignmentsChange"
      @update:timeFilter="onTimeFilterChange"
    />

    <div class="metrics-student-view__content">
      <metrics-overall-summary
        v-if="currentData.insights && currentData.insights.overallSummary"
        :summary="currentData.insights.overallSummary"
      />

      <metrics-student-progress-chart
        :chart-data="currentData.progressChart"
        :rows="currentData.progressChart ? currentData.progressChart.table : []"
        :student-name="studentName"
        :is-loading="isProgressChartLoading"
        :empty-description="emptyDescription"
      />

      <metrics-student-class-ranking
        :ranking="currentData.classRanking"
        :is-loading="isClassRankingLoading"
        :empty-description="emptyDescription"
      />

      <metrics-writing-skills-trajectory
        :insights="writingSkillsInsights"
        :is-loading="isInsightsLoading"
      />
    </div>

    <metrics-pdf-overlay v-if="isExportingPdf" />
  </div>
</template>

<script>
import MetricsClassHeader from "./components/class/metrics_class_header.vue";
import MetricsPdfOverlay from "./components/shared/metrics_pdf_overlay.vue";
import MetricsStudentFilters from "./components/student/metrics_student_filters.vue";
import MetricsOverallSummary from "./components/class/metrics_overall_summary.vue";
import MetricsStudentClassRanking from "./components/student/metrics_student_class_ranking.vue";
import MetricsStudentProgressChart from "./components/student/metrics_student_progress_chart.vue";
import MetricsWritingSkillsTrajectory from "./components/shared/metrics_writing_skills_trajectory.vue";
import metricsViewMixin from "@/mixins/metricsViewMixin.js";
import {
  metricsStudentDemoData,
  metricsStudentsDemoData,
} from "./demo_data/student.js";
import { METRICS_TIME_FILTERS } from "./demo_data/shared.js";
import {
  getMetricsStudentProgressChart,
  getMetricsStudentClassRanking,
  getMetricsStudentInsights,
} from "@/api/requests/metrics.js";

const LS_KEY_DEMO_MODE = "metrics-is-demo-mode";
const LS_KEY_TIME_FILTER = "metrics-student-time-filter";

export default {
  name: "MetricsStudentView",

  components: {
    MetricsClassHeader,
    MetricsPdfOverlay,
    MetricsStudentFilters,
    MetricsOverallSummary,
    MetricsStudentClassRanking,
    MetricsStudentProgressChart,
    MetricsWritingSkillsTrajectory,
  },

  mixins: [metricsViewMixin],

  inject: ["workspacesProvider"],

  data() {
    const savedDemo = localStorage.getItem(LS_KEY_DEMO_MODE);
    const savedFilter = localStorage.getItem(LS_KEY_TIME_FILTER);

    return {
      isDemoMode: savedDemo !== null ? JSON.parse(savedDemo) : false,
      timeFilter:
        savedFilter && METRICS_TIME_FILTERS.some((f) => f.value === savedFilter)
          ? savedFilter
          : METRICS_TIME_FILTERS[0].value,
      selectedClassIds: null,
      selectedAssignmentIds: null,

      progressChartData: null,
      isProgressChartLoading: false,
      allAssignments: [],

      classRankingData: null,
      isClassRankingLoading: false,
      cachedStudentName: "",

      insightsData: null,
      isInsightsLoading: false,

      isExportingPdf: false,
      enteredInDemoMode: false,
    };
  },

  computed: {
    studentId() {
      return this.$route.params.studentId;
    },

    currentDemoData() {
      const id = Number(this.studentId);
      return metricsStudentsDemoData[id] || metricsStudentDemoData;
    },

    classId() {
      return this.$route.params.classId;
    },

    pdfFileName() {
      return "student-overview";
    },

    isPageLoading() {
      return (
        this.isProgressChartLoading ||
        this.isClassRankingLoading ||
        this.isInsightsLoading
      );
    },

    pdfSubjectName() {
      return this.studentName ? `(${this.studentName})` : "";
    },

    studentName() {
      if (this.isDemoMode) return this.currentDemoData.studentName;
      if (this.cachedStudentName) return this.cachedStudentName;
      const current = this.classRankingData?.data?.find(
        (student) => student.isCurrentStudent
      );
      return current ? `${current.firstName} ${current.lastName}`.trim() : "";
    },

    availableAssignments() {
      if (this.isDemoMode) {
        return this.currentDemoData.progressChart.table.map((row) => ({
          id: row.assignmentId,
          name: row.assignmentName,
        }));
      }
      return this.allAssignments;
    },

    currentData() {
      if (this.isDemoMode) return this.currentDemoData;
      return {
        progressChart: this.progressChartData,
        classRanking: this.classRankingData,
        insights: this.insightsData,
      };
    },

    emptyDescription() {
      const isFiltered =
        this.selectedAssignmentIds !== null ||
        this.timeFilter !== METRICS_TIME_FILTERS[0].value;
      return isFiltered ? this.$t("metrics.noResultsForFilters") : null;
    },

    writingSkillsInsights() {
      const insights = this.currentData.insights;
      if (!insights) return null;
      return { summaryTrajectory: insights };
    },

    timeParams() {
      const filter = METRICS_TIME_FILTERS.find(
        (f) => f.value === this.timeFilter
      );
      return filter ? filter.getParams() : {};
    },
  },

  watch: {
    studentId() {
      if (!this.isDemoMode) {
        this.timeFilter = METRICS_TIME_FILTERS[0].value;
        localStorage.setItem(LS_KEY_TIME_FILTER, this.timeFilter);
        this.selectedAssignmentIds = null;
        this.allAssignments = [];
        this.cachedStudentName = "";
        this.fetchAll();
      }
    },
    classId() {
      if (!this.isDemoMode) {
        this.timeFilter = METRICS_TIME_FILTERS[0].value;
        localStorage.setItem(LS_KEY_TIME_FILTER, this.timeFilter);
        this.selectedAssignmentIds = null;
        this.allAssignments = [];
        this.fetchAll();
      }
    },
  },

  mounted() {
    this.enteredInDemoMode = this.isDemoMode;
    this.timeFilter = METRICS_TIME_FILTERS[0].value;
    localStorage.setItem(LS_KEY_TIME_FILTER, this.timeFilter);
    if (!this.isDemoMode) {
      this.fetchAll();
    }
  },

  methods: {
    onDemoModeChange(value) {
      this.isDemoMode = value;
      localStorage.setItem(LS_KEY_DEMO_MODE, JSON.stringify(value));
      if (!value) {
        if (this.enteredInDemoMode) {
          this.$router.push(this.metricsOverviewPath);
          return;
        }
        this.progressChartData = null;
        this.classRankingData = null;
        this.insightsData = null;
        this.cachedStudentName = "";
        this.fetchAll();
      }
    },

    onTimeFilterChange(value) {
      this.timeFilter = value;
      localStorage.setItem(LS_KEY_TIME_FILTER, value);
      this.fetchProgressChart();
      this.fetchClassRanking();
    },

    onSelectedAssignmentsChange(value) {
      this.selectedAssignmentIds = value;
      this.fetchProgressChart();
      this.fetchClassRanking();
    },

    onRegenerate() {
      this.progressChartData = null;
      this.classRankingData = null;
      this.insightsData = null;
      this.cachedStudentName = "";
      this.fetchAll();
    },

    fetchAll() {
      this.fetchProgressChart();
      this.fetchClassRanking();
      this.fetchInsights();
    },

    async fetchProgressChart() {
      if (!this.studentId || this.isDemoMode) return;
      this.isProgressChartLoading = true;
      try {
        const result = await getMetricsStudentProgressChart({
          entityId: this.entityId,
          studentId: this.studentId,
          classIds: this.selectedClassIds ?? [Number(this.classId)],
          assignmentIds: this.selectedAssignmentIds ?? undefined,
          dateFrom: this.timeParams.dateFrom,
          dateTo: this.timeParams.dateTo,
        });
        this.progressChartData = result?.data || null;
        if (
          this.selectedAssignmentIds === null &&
          this.progressChartData?.table?.length
        ) {
          this.allAssignments = this.progressChartData.table.map((row) => ({
            id: row.assignmentId,
            name: row.assignmentName,
          }));
        }
      } catch {
        this.progressChartData = null;
      } finally {
        this.isProgressChartLoading = false;
      }
    },

    async fetchClassRanking() {
      if (!this.studentId || this.isDemoMode) return;
      this.isClassRankingLoading = true;
      try {
        const result = await getMetricsStudentClassRanking({
          entityId: this.entityId,
          studentId: this.studentId,
          classIds: this.selectedClassIds ?? [Number(this.classId)],
          assignmentIds: this.selectedAssignmentIds ?? undefined,
          dateFrom: this.timeParams.dateFrom,
          dateTo: this.timeParams.dateTo,
        });
        this.classRankingData = result?.data || null;
        if (!this.cachedStudentName && this.classRankingData?.data) {
          const current = this.classRankingData.data.find(
            (student) => student.isCurrentStudent
          );
          if (current) {
            this.cachedStudentName =
              `${current.firstName} ${current.lastName}`.trim();
          }
        }
      } catch {
        this.classRankingData = null;
      } finally {
        this.isClassRankingLoading = false;
      }
    },

    async fetchInsights() {
      if (!this.studentId || this.isDemoMode) return;
      this.isInsightsLoading = true;
      try {
        const result = await getMetricsStudentInsights({
          entityId: this.entityId,
          studentId: this.studentId,
        });
        this.insightsData = result?.data || null;
      } catch {
        this.insightsData = null;
      } finally {
        this.isInsightsLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./components/shared/metrics_card";

.metrics-student-view {
  @include metrics-view;
  gap: 16px;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 24px;
  }
  ::v-deep {
    .metrics-class-header__left {
      min-height: auto;
    }
    .metrics-overall-summary__body {
      background-color: var(--color-surface-primary-light-1);
    }
  }
}
</style>
